import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CheckSquare,
  FileText,
  Globe,
  Search,
  UserRound,
  X,
} from 'lucide-react';
import type {
  Client,
  Proposal,
  ProposalStatus,
  ProposalType,
  ProposalVisibility,
  Task,
  Website,
} from '@oneup/shared';
import {
  clientsApi,
  communicationsApi,
  proposalsApi,
  tasksApi,
  usersApi,
  websitesApi,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormDrawer } from '@/components/form-drawer';
import { OrganizationPicker } from '@/components/organization-picker';
import { useFieldOptions } from '@/lib/use-field-options';
import { useUiStore, type CreateDrawerState, type CreateTaskInitial } from '@/stores/ui';
import { cn } from '@/lib/utils';

const PROPOSAL_TYPES: ProposalType[] = [
  'Website Build',
  'Webmaster Package',
  'Local SEO',
  'AI Voice Agent',
  'Automation',
  'Marketing Retainer',
  'Technical Estimate',
  'Custom',
];

const PROPOSAL_STATUSES: ProposalStatus[] = [
  'Draft',
  'Submitted',
  'Approved',
  'Rejected',
  'Invoiced',
];
const PROPOSAL_VISIBILITIES: ProposalVisibility[] = [
  'Draft',
  'Admin Only',
  'Client Visible',
  'Archived',
];

export function CreateRecordDrawer() {
  const drawer = useUiStore((state) => state.createDrawer);
  const closeCreateDrawer = useUiStore((state) => state.closeCreateDrawer);

  if (!drawer) return null;

  if (drawer.type === 'task') {
    return <CreateTaskDrawer key={drawer.nonce} drawer={drawer} onClose={closeCreateDrawer} />;
  }
  if (drawer.type === 'proposal') {
    return <CreateProposalDrawer key={drawer.nonce} onClose={closeCreateDrawer} />;
  }
  if (drawer.type === 'client') {
    return <CreateClientDrawer key={drawer.nonce} onClose={closeCreateDrawer} />;
  }
  if (drawer.type === 'contact') {
    return <CreateContactDrawer key={drawer.nonce} onClose={closeCreateDrawer} />;
  }
  return <CreateWebsiteDrawer key={drawer.nonce} onClose={closeCreateDrawer} />;
}

interface TaskFormState {
  status: string;
  clientId?: string;
  clientName?: string;
  websiteId?: string;
  websiteName?: string;
  parentTaskId?: string;
  parentTaskName?: string;
  name: string;
  notes: string;
  dueDate: string;
  priority: string;
}

function CreateTaskDrawer({ drawer, onClose }: { drawer: CreateDrawerState; onClose: () => void }) {
  const initial = drawer.values;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialState = useMemo(() => buildTaskInitialState(initial), [initial]);
  const [form, setForm] = useState<TaskFormState>(initialState);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, []);

  const websitesQuery = useQuery({
    queryKey: ['websites', { clientId: form.clientId, picker: 'create-task' }],
    queryFn: () => websitesApi.list({ clientId: form.clientId }),
    enabled: Boolean(form.clientId),
    staleTime: 5 * 60_000,
  });
  const parentTasksQuery = useQuery({
    queryKey: ['tasks', { clientId: form.clientId, picker: 'create-task-parent' }],
    queryFn: () => tasksApi.list({ clientId: form.clientId, limit: 300 }),
    enabled: Boolean(form.clientId),
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const task = await tasksApi.create({
        name: form.name.trim(),
        notes: clean(form.notes),
        status: form.status || undefined,
        clientId: form.clientId,
        websiteId: form.websiteId,
        parentTaskId: form.parentTaskId,
        priority: clean(form.priority),
        dueDate: form.dueDate || undefined,
        source: initial?.source ?? 'portal',
        type: initial?.taskType,
      });

      if (initial?.gmailRecordId) {
        try {
          await communicationsApi.patchLinks(initial.gmailRecordId, {
            clientId: form.clientId ?? null,
            userId: initial.gmailUserId,
            taskId: task.id,
            websiteId: form.websiteId ?? null,
          });
        } catch (err) {
          return {
            task,
            linkError:
              err instanceof Error
                ? err.message
                : 'Created task, but the Gmail record could not be linked.',
          };
        }
      }

      return { task, linkError: null };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (initial?.gmailRecordId) {
        void queryClient.invalidateQueries({ queryKey: ['communications'] });
        void queryClient.invalidateQueries({
          queryKey: ['communication-detail', initial.gmailRecordId],
        });
      }

      if (result.linkError) {
        setLinkError(
          `Created task "${result.task.name}" (${result.task.id}), but linking failed: ${result.linkError}`,
        );
        return;
      }

      setNotice(`Created task: ${result.task.name}`);
      onClose();
      if (!initial?.gmailRecordId) navigate(`/tasks/${result.task.id}`);
    },
    onError: (err) => {
      setLinkError(err instanceof Error ? err.message : 'Task creation failed.');
    },
  });

  const canSubmit = form.name.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!canSubmit) return;
    setLinkError(null);
    createMutation.mutate();
  };
  const reset = () => {
    setForm(initialState);
    setLinkError(null);
    nameInputRef.current?.focus();
  };

  return (
    <FormDrawer
      title={initial?.gmailRecordId ? 'Create Task From Email' : 'Create Task'}
      description={
        initial?.sourceTitle ? (
          <>
            <Badge variant="success">{initial.sourceLabel ?? 'Source'}</Badge>
            <span>{initial.sourceTitle}</span>
          </>
        ) : (
          'Add the task once, then open it anywhere work is referenced.'
        )
      }
      icon={CheckSquare}
      submitLabel="Create Task"
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={onClose}
      onSubmit={submit}
      onReset={reset}
      error={
        linkError ? (
          <InlineError>{linkError}</InlineError>
        ) : createMutation.isError ? (
          <InlineError>
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : 'Failed to create task'}
          </InlineError>
        ) : null
      }
    >
      <div className="grid gap-3 md:grid-cols-3">
        <CreateField label="Status">
          <TaskStatusSelect
            value={form.status}
            onChange={(status) => setForm((current) => ({ ...current, status }))}
          />
        </CreateField>
        <CreateField label="Target Date">
          <Input
            type="date"
            value={form.dueDate}
            onChange={(event) =>
              setForm((current) => ({ ...current, dueDate: event.target.value }))
            }
            className="h-9 text-[13px]"
          />
        </CreateField>
        <CreateField label="Priority">
          <Input
            value={form.priority}
            onChange={(event) =>
              setForm((current) => ({ ...current, priority: event.target.value }))
            }
            placeholder="Optional"
            className="h-9 text-[13px]"
          />
        </CreateField>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <CreateField label="Organization">
          <OrganizationPicker
            value={form.clientId}
            valueLabel={form.clientName}
            onChange={(clientId, clientName) =>
              setForm((current) => ({
                ...current,
                clientId,
                clientName,
                websiteId: current.clientId === clientId ? current.websiteId : undefined,
                websiteName: current.clientId === clientId ? current.websiteName : undefined,
                parentTaskId: current.clientId === clientId ? current.parentTaskId : undefined,
                parentTaskName: current.clientId === clientId ? current.parentTaskName : undefined,
              }))
            }
            onClear={() =>
              setForm((current) => ({
                ...current,
                clientId: undefined,
                clientName: undefined,
                websiteId: undefined,
                websiteName: undefined,
                parentTaskId: undefined,
                parentTaskName: undefined,
              }))
            }
          />
        </CreateField>
        <CreateField label="Website">
          <RecordPicker
            placeholder={form.clientId ? '+ Add website' : 'Choose organization first'}
            disabled={!form.clientId}
            value={form.websiteId}
            valueLabel={form.websiteName}
            options={websitesQuery.data?.items ?? []}
            getKey={(website) => website.id}
            getLabel={(website) => website.friendlyName ?? website.url ?? website.id}
            getMeta={(website) => website.url?.replace(/^https?:\/\//, '').slice(0, 28)}
            emptyLabel={
              form.clientId ? 'No websites for this organization.' : 'Choose an organization first.'
            }
            loading={websitesQuery.isLoading}
            searchPlaceholder="Search websites..."
            onChange={(website) =>
              setForm((current) => ({
                ...current,
                websiteId: website.id,
                websiteName: website.friendlyName ?? website.url ?? website.id,
              }))
            }
            onClear={() =>
              setForm((current) => ({
                ...current,
                websiteId: undefined,
                websiteName: undefined,
              }))
            }
          />
        </CreateField>
        <CreateField label="Parent Task">
          <RecordPicker
            placeholder={form.clientId ? '+ Add project' : 'Choose organization first'}
            disabled={!form.clientId}
            value={form.parentTaskId}
            valueLabel={form.parentTaskName}
            options={parentTasksQuery.data?.items ?? []}
            getKey={(task) => task.id}
            getLabel={(task) => task.name}
            getMeta={(task) => task.status}
            emptyLabel={
              form.clientId
                ? 'No tasks for this organization yet.'
                : 'Choose an organization first.'
            }
            loading={parentTasksQuery.isLoading}
            searchPlaceholder="Search parent tasks..."
            onChange={(task) =>
              setForm((current) => ({
                ...current,
                parentTaskId: task.id,
                parentTaskName: task.name,
              }))
            }
            onClear={() =>
              setForm((current) => ({
                ...current,
                parentTaskId: undefined,
                parentTaskName: undefined,
              }))
            }
          />
        </CreateField>
      </div>

      <CreateField label="Task Name">
        <Input
          ref={nameInputRef}
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder="What's the task?"
          className="h-9 text-[13px]"
          required
        />
      </CreateField>

      <CreateField label="Task Notes">
        <TextArea
          value={form.notes}
          rows={initial?.gmailRecordId ? 10 : 5}
          placeholder="Context, links, or follow-up notes"
          onChange={(notes) => setForm((current) => ({ ...current, notes }))}
        />
      </CreateField>
    </FormDrawer>
  );
}

function TaskStatusSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { data, isLoading } = useFieldOptions('Status');
  const options = useMemo(() => {
    const fromSchema = data?.options;
    if (fromSchema && fromSchema.length > 0) return fromSchema.map((option) => option.name);
    return ['Open', 'In Progress', 'Waiting on Client', 'Review', 'Closed'];
  }, [data?.options]);

  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-9">
        <SelectValue placeholder={isLoading ? 'Loading...' : 'Select...'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ClientFormState {
  name: string;
  status: string;
  relationshipType: string;
  website: string;
  hourlyRate: string;
  notes: string;
}

function CreateClientDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialState: ClientFormState = {
    name: '',
    status: 'Active',
    relationshipType: '',
    website: '',
    hourlyRate: '',
    notes: '',
  };
  const [form, setForm] = useState<ClientFormState>(initialState);

  useEffect(() => {
    const handle = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, []);

  const createMutation = useMutation({
    mutationFn: () =>
      clientsApi.create({
        name: form.name.trim(),
        status: clean(form.status),
        relationshipType: clean(form.relationshipType),
        website: clean(form.website),
        hourlyRate: parseOptionalNumber(form.hourlyRate),
        notes: clean(form.notes),
      }),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      setNotice(`Created organization: ${client.name}`);
      onClose();
      navigate(`/clients/${client.id}`);
    },
  });

  const canSubmit = form.name.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (canSubmit) createMutation.mutate();
  };

  return (
    <FormDrawer
      title="Create Organization"
      description="Add a client or organization record that can be linked to tasks, contacts, websites, and proposals."
      icon={Building2}
      submitLabel="Create Organization"
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={onClose}
      onSubmit={submit}
      onReset={() => {
        setForm(initialState);
        nameInputRef.current?.focus();
      }}
      error={mutationError(createMutation.error, 'Failed to create organization')}
    >
      <CreateField label="Organization Name">
        <Input
          ref={nameInputRef}
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          required
        />
      </CreateField>
      <div className="grid gap-3 md:grid-cols-3">
        <CreateField label="Status">
          <Input
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Relationship">
          <Input
            value={form.relationshipType}
            onChange={(event) =>
              setForm((current) => ({ ...current, relationshipType: event.target.value }))
            }
            placeholder="Direct Client"
          />
        </CreateField>
        <CreateField label="Hourly Rate">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.hourlyRate}
            onChange={(event) =>
              setForm((current) => ({ ...current, hourlyRate: event.target.value }))
            }
            placeholder="Optional"
          />
        </CreateField>
      </div>
      <CreateField label="Website">
        <Input
          value={form.website}
          onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))}
          placeholder="https://example.com"
        />
      </CreateField>
      <CreateField label="Notes">
        <TextArea
          value={form.notes}
          rows={4}
          onChange={(notes) => setForm((current) => ({ ...current, notes }))}
        />
      </CreateField>
    </FormDrawer>
  );
}

interface ContactFormState {
  fullName: string;
  email: string;
  phone: string;
  title: string;
  status: string;
  userType: string;
  clientId: string;
  clientName: string;
}

function CreateContactDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialState: ContactFormState = {
    fullName: '',
    email: '',
    phone: '',
    title: '',
    status: 'Active',
    userType: '',
    clientId: '',
    clientName: '',
  };
  const [form, setForm] = useState<ContactFormState>(initialState);

  useEffect(() => {
    const handle = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, []);

  const createMutation = useMutation({
    mutationFn: () =>
      usersApi.create({
        fullName: form.fullName.trim(),
        email: clean(form.email),
        phone: clean(form.phone),
        title: clean(form.title),
        status: clean(form.status),
        userTypes: clean(form.userType) ? [form.userType.trim()] : undefined,
        clientIds: form.clientId ? [form.clientId] : undefined,
      }),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      setNotice(`Created contact: ${user.fullName}`);
      onClose();
      navigate(`/users/${user.id}`);
    },
  });

  const canSubmit = form.fullName.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (canSubmit) createMutation.mutate();
  };

  return (
    <FormDrawer
      title="Create Contact"
      description="Add a person record and optionally connect it to an organization."
      icon={UserRound}
      submitLabel="Create Contact"
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={onClose}
      onSubmit={submit}
      onReset={() => {
        setForm(initialState);
        nameInputRef.current?.focus();
      }}
      error={mutationError(createMutation.error, 'Failed to create contact')}
    >
      <CreateField label="Full Name">
        <Input
          ref={nameInputRef}
          value={form.fullName}
          onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
          required
        />
      </CreateField>
      <div className="grid gap-3 md:grid-cols-2">
        <CreateField label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Phone">
          <Input
            value={form.phone}
            onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
          />
        </CreateField>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <CreateField label="Title">
          <Input
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Status">
          <Input
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Type">
          <Input
            value={form.userType}
            onChange={(event) =>
              setForm((current) => ({ ...current, userType: event.target.value }))
            }
            placeholder="Client"
          />
        </CreateField>
      </div>
      <CreateField label="Organization">
        <OrganizationPicker
          value={form.clientId}
          valueLabel={form.clientName}
          onChange={(clientId, clientName) =>
            setForm((current) => ({ ...current, clientId, clientName }))
          }
          onClear={() => setForm((current) => ({ ...current, clientId: '', clientName: '' }))}
        />
      </CreateField>
    </FormDrawer>
  );
}

interface WebsiteFormState {
  url: string;
  friendlyName: string;
  status: string;
  type: string;
  hostingPlatform: string;
  hostedBy: string;
  organizationId: string;
  organizationName: string;
}

function CreateWebsiteDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const initialState: WebsiteFormState = {
    url: '',
    friendlyName: '',
    status: 'Live',
    type: '',
    hostingPlatform: '',
    hostedBy: '',
    organizationId: '',
    organizationName: '',
  };
  const [form, setForm] = useState<WebsiteFormState>(initialState);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => urlInputRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, []);

  const createMutation = useMutation({
    mutationFn: () =>
      websitesApi.create({
        url: form.url.trim(),
        friendlyName: clean(form.friendlyName),
        status: clean(form.status),
        type: clean(form.type),
        hostingPlatform: clean(form.hostingPlatform),
        hostedBy: clean(form.hostedBy),
        organizationId: form.organizationId || undefined,
      }),
    onSuccess: (website) => {
      void queryClient.invalidateQueries({ queryKey: ['websites'] });
      setNotice(`Created website: ${website.friendlyName ?? website.url}`);
      onClose();
      navigate(`/websites/${website.id}`);
    },
  });

  const canSubmit = form.url.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!canSubmit) return;
    if (!isHttpUrl(form.url.trim())) {
      setValidationError('Enter a valid http or https URL.');
      return;
    }
    setValidationError(null);
    createMutation.mutate();
  };

  return (
    <FormDrawer
      title="Create Website"
      description="Add a website record and link it to the organization it belongs to."
      icon={Globe}
      submitLabel="Create Website"
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={onClose}
      onSubmit={submit}
      onReset={() => {
        setForm(initialState);
        setValidationError(null);
        urlInputRef.current?.focus();
      }}
      error={
        validationError ? (
          <InlineError>{validationError}</InlineError>
        ) : (
          mutationError(createMutation.error, 'Failed to create website')
        )
      }
    >
      <CreateField label="Website URL">
        <Input
          ref={urlInputRef}
          value={form.url}
          onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
          placeholder="https://example.com"
          required
        />
      </CreateField>
      <div className="grid gap-3 md:grid-cols-2">
        <CreateField label="Friendly Name">
          <Input
            value={form.friendlyName}
            onChange={(event) =>
              setForm((current) => ({ ...current, friendlyName: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Organization">
          <OrganizationPicker
            value={form.organizationId}
            valueLabel={form.organizationName}
            onChange={(organizationId, organizationName) =>
              setForm((current) => ({ ...current, organizationId, organizationName }))
            }
            onClear={() =>
              setForm((current) => ({ ...current, organizationId: '', organizationName: '' }))
            }
          />
        </CreateField>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <CreateField label="Status">
          <Input
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Type">
          <Input
            value={form.type}
            onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
          />
        </CreateField>
        <CreateField label="Hosted By">
          <Input
            value={form.hostedBy}
            onChange={(event) =>
              setForm((current) => ({ ...current, hostedBy: event.target.value }))
            }
          />
        </CreateField>
      </div>
      <CreateField label="Hosting Platform">
        <Input
          value={form.hostingPlatform}
          onChange={(event) =>
            setForm((current) => ({ ...current, hostingPlatform: event.target.value }))
          }
        />
      </CreateField>
    </FormDrawer>
  );
}

interface ProposalFormState {
  proposalName: string;
  proposalType: ProposalType | '';
  status: ProposalStatus;
  portalVisibility: ProposalVisibility;
  proposalDate: string;
  validUntil: string;
  proposalAmount: string;
  depositAmount: string;
  timeline: string;
  scopeSummary: string;
  deliverables: string;
  paymentTerms: string;
  clientFacingNextStep: string;
  internalNextAction: string;
  notes: string;
  organizationId: string;
  organizationName: string;
  leadId: string;
}

function CreateProposalDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const initialState = useMemo(() => buildProposalInitialState(), []);
  const [form, setForm] = useState<ProposalFormState>(initialState);

  useEffect(() => {
    const handle = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(handle);
  }, []);

  const createMutation = useMutation({
    mutationFn: () => proposalsApi.create(toProposalCreatePayload(form)),
    onSuccess: (proposal) => {
      void queryClient.invalidateQueries({ queryKey: ['proposals'] });
      setNotice(`Created proposal: ${proposal.proposalName}`);
      onClose();
      navigate(`/proposals/${proposal.id}`);
    },
  });

  const canSubmit = form.proposalName.trim().length > 0 && !createMutation.isPending;
  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (canSubmit) createMutation.mutate();
  };

  return (
    <FormDrawer
      title="Create Proposal"
      description="Create an Airtable proposal draft for review and document generation."
      icon={FileText}
      widthClassName="sm:max-w-[720px] md:max-w-[820px]"
      submitLabel="Create Proposal"
      canSubmit={canSubmit}
      pending={createMutation.isPending}
      onClose={onClose}
      onSubmit={submit}
      onReset={() => {
        setForm(buildProposalInitialState());
        nameInputRef.current?.focus();
      }}
      error={mutationError(createMutation.error, 'Failed to create proposal')}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CreateField label="Proposal Name" className="sm:col-span-2">
          <Input
            ref={nameInputRef}
            value={form.proposalName}
            onChange={(event) =>
              setForm((current) => ({ ...current, proposalName: event.target.value }))
            }
            required
          />
        </CreateField>
        <CreateField label="Proposal Type">
          <Select
            value={form.proposalType || undefined}
            onValueChange={(proposalType) =>
              setForm((current) => ({
                ...current,
                proposalType: proposalType as ProposalType,
              }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {PROPOSAL_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CreateField>
        <CreateField label="Status">
          <Select
            value={form.status}
            onValueChange={(status) =>
              setForm((current) => ({ ...current, status: status as ProposalStatus }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPOSAL_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CreateField>
        <CreateField label="Portal Visibility">
          <Select
            value={form.portalVisibility}
            onValueChange={(portalVisibility) =>
              setForm((current) => ({
                ...current,
                portalVisibility: portalVisibility as ProposalVisibility,
              }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPOSAL_VISIBILITIES.map((visibility) => (
                <SelectItem key={visibility} value={visibility}>
                  {visibility}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CreateField>
        <CreateField label="Proposal Date">
          <Input
            type="date"
            value={form.proposalDate}
            onChange={(event) =>
              setForm((current) => ({ ...current, proposalDate: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Valid Until">
          <Input
            type="date"
            value={form.validUntil}
            onChange={(event) =>
              setForm((current) => ({ ...current, validUntil: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Proposal Amount">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.proposalAmount}
            onChange={(event) =>
              setForm((current) => ({ ...current, proposalAmount: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Deposit Amount">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.depositAmount}
            onChange={(event) =>
              setForm((current) => ({ ...current, depositAmount: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Organization">
          <OrganizationPicker
            value={form.organizationId}
            valueLabel={form.organizationName}
            onChange={(organizationId, organizationName) =>
              setForm((current) => ({ ...current, organizationId, organizationName }))
            }
            onClear={() =>
              setForm((current) => ({ ...current, organizationId: '', organizationName: '' }))
            }
          />
        </CreateField>
        <CreateField label="Lead Record ID">
          <Input
            value={form.leadId}
            onChange={(event) => setForm((current) => ({ ...current, leadId: event.target.value }))}
            placeholder="rec..."
          />
        </CreateField>
        <CreateField label="Timeline" className="sm:col-span-2">
          <Input
            value={form.timeline}
            onChange={(event) =>
              setForm((current) => ({ ...current, timeline: event.target.value }))
            }
          />
        </CreateField>
        <CreateField label="Scope Summary" className="sm:col-span-2">
          <TextArea
            value={form.scopeSummary}
            onChange={(scopeSummary) => setForm((current) => ({ ...current, scopeSummary }))}
          />
        </CreateField>
        <CreateField label="Deliverables" className="sm:col-span-2">
          <TextArea
            value={form.deliverables}
            onChange={(deliverables) => setForm((current) => ({ ...current, deliverables }))}
          />
        </CreateField>
        <CreateField label="Payment Terms" className="sm:col-span-2">
          <TextArea
            value={form.paymentTerms}
            onChange={(paymentTerms) => setForm((current) => ({ ...current, paymentTerms }))}
          />
        </CreateField>
        <CreateField label="Client-Facing Next Step" className="sm:col-span-2">
          <TextArea
            value={form.clientFacingNextStep}
            onChange={(clientFacingNextStep) =>
              setForm((current) => ({ ...current, clientFacingNextStep }))
            }
          />
        </CreateField>
        <CreateField label="Internal Next Action" className="sm:col-span-2">
          <TextArea
            value={form.internalNextAction}
            onChange={(internalNextAction) =>
              setForm((current) => ({ ...current, internalNextAction }))
            }
          />
        </CreateField>
        <CreateField label="Notes" className="sm:col-span-2">
          <TextArea
            value={form.notes}
            onChange={(notes) => setForm((current) => ({ ...current, notes }))}
          />
        </CreateField>
      </div>
    </FormDrawer>
  );
}

function CreateField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('flex flex-col gap-1', className)}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextArea({
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

function RecordPicker<T>({
  placeholder,
  value,
  valueLabel,
  options,
  getKey,
  getLabel,
  getMeta,
  emptyLabel,
  loading,
  disabled,
  searchPlaceholder,
  onChange,
  onClear,
}: {
  placeholder: string;
  value?: string;
  valueLabel?: string;
  options: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getMeta?: (item: T) => string | undefined;
  emptyLabel: string;
  loading?: boolean;
  disabled?: boolean;
  searchPlaceholder: string;
  onChange: (item: T) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const displayValue = valueLabel || value;
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((option) => {
      const label = getLabel(option).toLowerCase();
      const meta = getMeta?.(option)?.toLowerCase() ?? '';
      return label.includes(q) || meta.includes(q);
    });
  }, [getLabel, getMeta, options, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(!open)}
            className={cn(
              'flex h-9 w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-2.5 text-left text-[12px]',
              displayValue ? 'pr-7' : 'pr-2.5',
              'transition-colors hover:border-foreground/20',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
          >
            <span
              className={cn('truncate', displayValue ? 'text-foreground' : 'text-muted-foreground')}
            >
              {displayValue ?? placeholder}
            </span>
            {!displayValue && (
              <Search className="h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
            )}
          </button>
        </PopoverTrigger>
        {displayValue && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="relative border-b border-border">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            autoFocus
            className="h-8 w-full bg-transparent pl-7 pr-2 text-[12px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <ul className="max-h-[260px] overflow-y-auto py-1">
          {loading && <li className="px-3 py-2 text-[12px] text-muted-foreground">Loading...</li>}
          {!loading && filtered.length === 0 && (
            <li className="px-3 py-2 text-[12px] text-muted-foreground">{emptyLabel}</li>
          )}
          {filtered.slice(0, 80).map((option) => {
            const key = getKey(option);
            const meta = getMeta?.(option);
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-muted focus-visible:bg-muted',
                    value === key && 'bg-primary/10 text-primary',
                  )}
                >
                  <span className="truncate">{getLabel(option)}</span>
                  {meta && (
                    <span className="ml-2 max-w-[45%] flex-shrink-0 truncate text-[10px] text-muted-foreground">
                      {meta}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function InlineError({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
      <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

function mutationError(error: unknown, fallback: string) {
  if (!error) return null;
  return <InlineError>{error instanceof Error ? error.message : fallback}</InlineError>;
}

function buildTaskInitialState(initial?: CreateTaskInitial): TaskFormState {
  return {
    status: initial?.status ?? 'Open',
    clientId: initial?.clientId,
    clientName: initial?.clientName,
    websiteId: initial?.websiteId,
    websiteName: initial?.websiteName,
    parentTaskId: undefined,
    parentTaskName: undefined,
    name: initial?.name ?? '',
    notes: initial?.notes ?? '',
    dueDate: initial?.dueDate ?? '',
    priority: initial?.priority ?? '',
  };
}

function buildProposalInitialState(): ProposalFormState {
  const today = new Date();
  const validUntil = new Date(today);
  validUntil.setDate(validUntil.getDate() + 14);
  return {
    proposalName: '',
    proposalType: '',
    status: 'Draft',
    portalVisibility: 'Admin Only',
    proposalDate: toDateInputValue(today),
    validUntil: toDateInputValue(validUntil),
    proposalAmount: '',
    depositAmount: '',
    timeline: '',
    scopeSummary: '',
    deliverables: '',
    paymentTerms: '',
    clientFacingNextStep: '',
    internalNextAction: '',
    notes: '',
    organizationId: '',
    organizationName: '',
    leadId: '',
  };
}

function toProposalCreatePayload(form: ProposalFormState): Partial<Proposal> & {
  proposalName: string;
} {
  return {
    proposalName: form.proposalName.trim(),
    proposalType: form.proposalType || undefined,
    status: form.status,
    portalVisibility: form.portalVisibility,
    proposalDate: form.proposalDate || undefined,
    validUntil: form.validUntil || undefined,
    proposalAmount: parseOptionalNumber(form.proposalAmount),
    depositAmount: parseOptionalNumber(form.depositAmount),
    timeline: clean(form.timeline),
    scopeSummary: clean(form.scopeSummary),
    deliverables: clean(form.deliverables),
    paymentTerms: clean(form.paymentTerms),
    clientFacingNextStep: clean(form.clientFacingNextStep),
    internalNextAction: clean(form.internalNextAction),
    notes: clean(form.notes),
    organizationIds: form.organizationId.trim() ? [form.organizationId.trim()] : undefined,
    leadIds: form.leadId.trim() ? [form.leadId.trim()] : undefined,
  };
}

function clean(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function parseOptionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
